import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../../components/ErrorBoundary';
import { supabase } from '../../lib/supabase';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  VStack,
  Text,
  Heading,
  useToast,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Divider,
  Image,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AuthError } from '@supabase/supabase-js';

// Custom error fallback for auth components
const AuthErrorFallback = ({ error, onReset }: { error: Error | null, onReset: () => void }) => {
  const [isReloading, setIsReloading] = useState(false);
  const navigate = useNavigate();

  const handleReset = () => {
    setIsReloading(true);
    onReset();
    window.location.reload();
  };

  return (
    <Container maxW="container.sm" py={10}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>
            We're having trouble with the authentication service. Please try refreshing the page.
          </AlertDescription>
          {process.env.NODE_ENV === 'development' && error && (
            <Box mt={2} p={2} bg="blackAlpha.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Error details:</Text>
              <Text as="pre" whiteSpace="pre-wrap" fontFamily="mono" fontSize="xs">
                {error.message}
              </Text>
            </Box>
          )}
        </Box>
      </Alert>
      
      <VStack spacing={4}>
        <Button
          colorScheme="blue"
          onClick={handleReset}
          isLoading={isReloading}
          loadingText="Reloading..."
          leftIcon={<Box as="span" className="material-icons">refresh</Box>}
        >
          Reload Page
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/login')}
          isDisabled={isReloading}
        >
          Back to Login
        </Button>
      </VStack>
    </Container>
  );
};

// Main LoginPage component
function LoginPageContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();

  // Tick down cooldown every second
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setCooldownUntil(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      navigate('/course');
    } catch (error) {
      const authError = error as AuthError;
      setAuthError(authError.message);
      
      toast({
        title: 'Error logging in',
        description: authError.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtpCode = async () => {
    if (!email) {
      toast({
        title: 'Enter your email',
        description: 'Please enter your email to receive a sign-in code.',
        status: 'info',
        duration: 4000,
      });
      return;
    }

    if (cooldownUntil && Date.now() < cooldownUntil) {
      return; // still cooling down
    }

    setIsSendingCode(true);
    setAuthError(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
        },
      });

      if (error) throw error;
      
      // Show OTP input
      setShowOtpInput(true);
      
      toast({
        title: 'Check your email',
        description: "We've sent a 6-digit code to your email address.",
        status: 'success',
        duration: 6000,
      });
    } catch (err) {
      console.error('Error sending OTP code:', err);
      // Generic message to avoid user enumeration
      toast({
        title: 'Check your email',
        description: "If that email exists in our system, we've sent a code to sign in.",
        status: 'success',
        duration: 6000,
      });
      // Still show OTP input even on error for better UX
      setShowOtpInput(true);
    } finally {
      setIsSendingCode(false);
      // Start 60s cooldown
      setCooldownUntil(Date.now() + 60_000);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter the 6-digit code from your email.',
        status: 'error',
        duration: 4000,
      });
      return;
    }

    setIsVerifyingOtp(true);
    setAuthError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (error) throw error;
      
      // Success! Navigate to course page
      navigate('/course');
    } catch (error) {
      const authError = error as AuthError;
      setAuthError(authError.message);
      
      toast({
        title: 'Invalid code',
        description: 'The code you entered is incorrect or has expired. Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResetOtpFlow = () => {
    setShowOtpInput(false);
    setOtp('');
    setAuthError(null);
    setCooldownUntil(null);
  };

  return (
    <Box minH="100vh" bg="#1AC8ED" p={4}>
      {/* Logo in top left */}
      <Container maxW="container.xl">
        <Box mb={8}>
          <Image 
            src="/RL Logo.svg" 
            alt="Rad Learning Logo" 
            h="50px"
            w="auto"
            onClick={() => navigate('/')}
            cursor="pointer"
            _hover={{
              opacity: 0.9,
              transform: 'translateY(-1px)',
            }}
            transition="all 0.2s"
          />
        </Box>
      </Container>

      {/* Centered login card */}
      <Container maxW="md" mt={8} mb={16}>
        <Box 
          bg="white" 
          p={10} 
          borderRadius="2xl" 
          boxShadow="sm"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <Stack spacing={8}>
            <Box textAlign="center">
              <Flex justify="center" mb={2}>
                <Image 
                  src="/Rad Learning Logo.svg" 
                  alt="Rad Learning Logo" 
                  h="150px"
                  w="auto"
                  mb={1}
                />
              </Flex>
              <Heading size="xl" fontWeight="semibold" mb={3} color="gray.800">
                Rad Learning Tutor
              </Heading>
              <Text color="gray.600" fontSize="lg">
                Login to your account
              </Text>
            </Box>

            {authError && (
              <Alert status="error" borderRadius="md" mb={4}>
                <AlertIcon />
                <Box>
                  <AlertTitle>Login Failed</AlertTitle>
                  <AlertDescription fontSize="sm">
                    {authError}
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            <Stack spacing={6}>
              <Stack spacing={4}>
                <FormControl id="email" isRequired isInvalid={!!authError}>
                  <FormLabel fontWeight="medium" color="gray.700">Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    size="lg"
                    bg="white"
                    borderColor={authError ? "red.300" : "gray.300"}
                    _hover={{ borderColor: authError ? "red.400" : 'gray.400' }}
                    _focus={{ 
                      borderColor: authError ? "red.500" : 'blue.400', 
                      boxShadow: authError ? '0 0 0 1px #e53e3e' : '0 0 0 1px #4299e1'
                    }}
                    autoComplete="email"
                    inputMode="email"
                    enterKeyHint="next"
                    spellCheck="false"
                  />
                </FormControl>

                {!showPasswordLogin && !showOtpInput && (
                  <Button
                    type="button"
                    onClick={handleSendOtpCode}
                    bg="#C200FB" // RL Purple as primary CTA
                    color="white"
                    _hover={{
                      bg: '#A800D6',
                      transform: 'translateY(-1px)',
                      boxShadow: 'lg',
                    }}
                    _active={{
                      bg: '#17FFC4',
                      color: 'black',
                      transform: 'translateY(0)',
                    }}
                    _focusVisible={{
                      bg: '#17FFC4',
                      color: 'black',
                      boxShadow: '0 0 0 3px rgba(24, 144, 255, 0.5)',
                    }}
                    size="lg"
                    width="full"
                    height="48px"
                    isLoading={isSendingCode}
                    loadingText="Sending code..."
                    isDisabled={isSendingCode || (!!cooldownUntil && Date.now() < cooldownUntil)}
                  >
                    {cooldownUntil && Date.now() < cooldownUntil
                      ? `Email me a code (${secondsLeft}s)`
                      : 'Email me a code'}
                  </Button>
                )}

                {!showPasswordLogin && showOtpInput && (
                  <form onSubmit={handleVerifyOtp}>
                    <Stack spacing={4}>
                      <FormControl id="otp" isRequired isInvalid={!!authError}>
                        <FormLabel fontWeight="medium" color="gray.700">
                          Enter 6-digit code
                        </FormLabel>
                        <Input
                          type="text"
                          value={otp}
                          onChange={(e) => {
                            // Only allow numbers and limit to 6 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(value);
                          }}
                          placeholder="000000"
                          size="lg"
                          bg="white"
                          borderColor={authError ? "red.300" : "gray.300"}
                          _hover={{ borderColor: authError ? "red.400" : 'gray.400' }}
                          _focus={{ 
                            borderColor: authError ? "red.500" : 'blue.400', 
                            boxShadow: authError ? '0 0 0 1px #e53e3e' : '0 0 0 1px #4299e1'
                          }}
                          textAlign="center"
                          fontSize="2xl"
                          letterSpacing="0.5em"
                          fontWeight="bold"
                          maxLength={6}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          enterKeyHint="done"
                          pattern="[0-9]*"
                        />
                        <Text fontSize="sm" color="gray.500" mt={2}>
                          Check your email for the 6-digit code
                        </Text>
                      </FormControl>

                      <Button
                        type="submit"
                        bg="#C200FB"
                        color="white"
                        _hover={{
                          bg: '#A800D6',
                          transform: 'translateY(-1px)',
                          boxShadow: 'lg',
                        }}
                        _active={{
                          bg: '#17FFC4',
                          color: 'black',
                          transform: 'translateY(0)',
                        }}
                        size="lg"
                        width="full"
                        height="48px"
                        isLoading={isVerifyingOtp}
                        loadingText="Verifying..."
                        isDisabled={otp.length !== 6}
                      >
                        Verify Code
                      </Button>

                      <Flex justify="space-between" align="center">
                        <Button
                          variant="link"
                          size="sm"
                          color="blue.500"
                          onClick={handleSendOtpCode}
                          isDisabled={!!cooldownUntil && Date.now() < cooldownUntil}
                        >
                          {cooldownUntil && Date.now() < cooldownUntil
                            ? `Resend code (${secondsLeft}s)`
                            : 'Resend code'}
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          color="gray.600"
                          onClick={handleResetOtpFlow}
                        >
                          Change email
                        </Button>
                      </Flex>
                    </Stack>
                  </form>
                )}

                {!showOtpInput && (
                  <Text color="gray.600" fontSize="sm" textAlign="center">
                    {showPasswordLogin ? 'Changed your mind? ' : 'Prefer a password? '}
                    <Link 
                      as="button" 
                      type="button" 
                      color="blue.500" 
                      fontWeight="medium"
                      onClick={() => {
                        setShowPasswordLogin((v) => !v);
                        setAuthError(null);
                      }}
                      _hover={{ textDecoration: 'none' }}
                    >
                      {showPasswordLogin ? 'Use email code instead' : 'Use password instead'}
                    </Link>
                  </Text>
                )}
              </Stack>

              {showPasswordLogin && (
                <>
                  <Divider />
                  <form onSubmit={handleLogin}>
                    <Stack spacing={6}>
                      <FormControl id="password" isRequired isInvalid={!!authError}>
                        <FormLabel mb={1} fontWeight="medium" color="gray.700">Password</FormLabel>
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          size="lg"
                          bg="white"
                          borderColor={authError ? "red.300" : "gray.300"}
                          _hover={{ borderColor: authError ? "red.400" : 'gray.400' }}
                          _focus={{ 
                            borderColor: authError ? "red.500" : 'blue.400', 
                            boxShadow: authError ? '0 0 0 1px #e53e3e' : '0 0 0 1px #4299e1'
                          }}
                          autoComplete="current-password"
                          enterKeyHint="done"
                        />
                        <Flex justify="flex-end" mt={1}>
                          <Link 
                            as="button" 
                            type="button" 
                            color="blue.500" 
                            fontSize="sm"
                            fontWeight="medium"
                            onClick={onOpen}
                            _hover={{ textDecoration: 'none' }}
                          >
                            Forgot password?
                          </Link>
                        </Flex>
                      </FormControl>

                      <Button
                        type="submit"
                        variant="outline"
                        bg="transparent"
                        borderWidth="2px"
                        borderColor="#C200FB"
                        color="#C200FB"
                        _hover={{
                          bg: 'rgba(194, 0, 251, 0.08)',
                          borderColor: '#A800D6',
                          color: '#A800D6',
                          transform: 'translateY(-1px)',
                          boxShadow: 'sm',
                        }}
                        _active={{
                          bg: 'rgba(23, 255, 196, 0.12)',
                          borderColor: '#17FFC4',
                          color: 'black',
                          transform: 'translateY(0)',
                        }}
                        _focusVisible={{
                          boxShadow: '0 0 0 3px rgba(24, 144, 255, 0.5)',
                          borderColor: '#C200FB',
                        }}
                        size="lg"
                        width="full"
                        height="48px"
                        isLoading={isLoading}
                        loadingText="Signing in..."
                        transition="all 0.2s"
                      >
                        Sign in with password
                      </Button>
                    </Stack>
                  </form>
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Container>
      
      <ResetPasswordModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}

// Reset Password Modal Component
function ResetPasswordModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Send OTP code for password reset
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) throw error;
      
      toast({
        title: 'Check your email',
        description: "We've sent a 6-digit code to reset your password.",
        status: 'success',
        duration: 5000,
      });
      
      // Navigate to update password page with email in state
      navigate('/update-password', { state: { email, isReset: true } });
      onClose();
    } catch (error) {
      const authError = error as AuthError;
      setError(authError.message);
      console.error('Error sending password reset code:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reset Password</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleResetPassword}>
          <ModalBody>
            <Stack spacing={4}>
              {error && (
                <Alert status="error" borderRadius="md" size="sm">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <Text>Enter your email address and we'll send you a 6-digit code to reset your password.</Text>
              <FormControl id="reset-email" isRequired>
                <FormLabel>Email address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  isInvalid={!!error}
                  autoComplete="email"
                  inputMode="email"
                  enterKeyHint="done"
                  spellCheck="false"
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button 
              colorScheme="blue" 
              type="submit"
              isLoading={isLoading}
              loadingText="Sending code..."
            >
              Send Code
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

// Export the wrapped component
export function LoginPage() {
  const [error, setError] = useState<Error | null>(null);

  return (
    <ErrorBoundary 
      fallback={
        <AuthErrorFallback 
          error={error} 
          onReset={() => setError(null)} 
        />
      }
      onError={(error: Error) => {
        setError(error);
        console.error('LoginPage error boundary caught an error:', error);
      }}
    >
      <LoginPageContent />
    </ErrorBoundary>
  );
}
